// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ErrorCause } from '../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';

// utils
import { translateError } from '../../../utils/translations';

// props
export interface DialogRetryTransactionProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogRetryTransaction = ({
	isOpen,
	onClose,
	onSuccess,
}: DialogRetryTransactionProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogTransaction = searchParams.get('dialogTransaction');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	// vars
	const idForm = 'form-retry-transaction';

	// retry fetch (lazy — triggered on submit)
	const { fetchRequest: fetchRetryTransaction } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/transactions/${paramDialogTransaction}/retry`,
		options: {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: () => {
			setBanner({
				hasClose: true,
				message: t('alert.transactionOpExRetried'),
				severity: 'success',
			});

			if (onSuccess) {
				onSuccess();
			}

			if (onClose) {
				onClose();
			}
		},
		onError: (err) => {
			const cause = err as ErrorCause;
			setFeedback({
				items: [
					{
						alert: {
							children: translateError({ cause, t }),
							severity: 'error',
						},
					},
				],
			});
			setIsLoading(false);
		},
		onComplete: () => {
			setIsLoading(false);
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsLoading(true);
		setFeedback(null);
		fetchRetryTransaction();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: t('retry'),
					color: 'primary',
					form: idForm,
					loading: isLoading,
					type: 'submit',
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'opex',
			}}
			isOpen={isOpen}
			maxWidth={500}
			onClose={handleClose}
			title={{
				children: t('retryOpEx'),
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<form id={idForm} onSubmit={handleSubmit}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography>{t('dialogRetryTransaction.content')}</Typography>
							</Grid>
							<Grid item xs={12}>
								<Alert severity="warning">
									<AlertTitle>{t('thisCannotBeUndone')}</AlertTitle>
									{t('alert.retryOpEx')}
								</Alert>
							</Grid>
							{feedback && (
								<Grid item xs={12}>
									<AlertFeedback {...feedback} />
								</Grid>
							)}
						</Grid>
					</form>
				</Grid>
			</Grid>
		</DialogActions>
	);
};
